/**
 * What is actually happening.
 *
 * Three questions an operator keeps having to answer by hand, on one page: how
 * much traffic there is and of what kind, how much of it the cache absorbs, and
 * how slow the sources really are.
 *
 * Latency is shown twice on purpose — overall, and for the calls that reached the
 * wire. At a 90% hit rate the overall average is a tenth of the real cost, so it
 * looks wonderful and says nothing about whether TecDoc is healthy. The wire
 * figures are the ones that move when something is wrong.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import { useNav } from '../lib/router';
import { Chip, DataBlock, Empty, Loading, Seg, SourceChip, Table } from '../ui';

interface UpstreamRow {
  source: string;
  op: string;
  mode: string;
  calls: number;
  hits: number;
  misses: number;
  errors: number;
  avgMs: number;
  wireP50Ms: number;
  wireP95Ms: number;
  wireMaxMs: number;
  bytes: string | number;
}
interface InboundRow {
  route: string;
  requests: number;
  errors: number;
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  upstreamCalls: number;
  withInjection: number;
}
interface TimelineRow {
  hour: string;
  calls: number;
  hits: number;
  errors: number;
  wireAvgMs: number;
}
interface TopProduct {
  productId: number;
  brand: string;
  article: string;
  name: string | null;
  hits: number;
  codes: number;
  lastAt: string;
}
interface Stats {
  upstream: UpstreamRow[];
  inbound: InboundRow[];
  timeline: TimelineRow[];
  products: TopProduct[];
}

const WINDOWS: { value: string; label: string; hours: number }[] = [
  { value: '1', label: 'Last hour', hours: 1 },
  { value: '24', label: '24 hours', hours: 24 },
  { value: '168', label: '7 days', hours: 168 },
  { value: '720', label: '30 days', hours: 720 },
];

const pct = (n: number, of: number): string => (of === 0 ? '—' : `${Math.round((n / of) * 100)}%`);
const fmtBytes = (n: number): string =>
  n > 1024 * 1024 * 1024
    ? `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`
    : n > 1024 * 1024
      ? `${(n / 1024 / 1024).toFixed(1)} MB`
      : `${Math.round(n / 1024)} KB`;

/** A bar per hour, scaled to the busiest one. Cache hits shaded underneath. */
function Timeline({ rows }: { rows: TimelineRow[] }) {
  if (rows.length === 0) return <Empty>No traffic in this window.</Empty>;
  const peak = Math.max(...rows.map((r) => r.calls), 1);
  return (
    <div className="flex items-end gap-[2px]" style={{ height: 90 }}>
      {rows.map((r) => (
        <div
          key={r.hour}
          className="relative flex-1 rounded-t bg-accent/30"
          style={{ height: `${Math.max((r.calls / peak) * 100, 2)}%`, minWidth: 3 }}
          title={`${r.hour} · ${r.calls} calls · ${r.hits} from cache · ${r.errors} errors · ${r.wireAvgMs}ms avg on the wire`}
        >
          <div
            className="absolute bottom-0 w-full rounded-t bg-accent"
            style={{ height: `${r.calls ? (r.hits / r.calls) * 100 : 0}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export default function Stats() {
  const { request } = useApi();
  const { go } = useNav();
  const [win, setWin] = useState('24');
  const [data, setData] = useState<Stats | null>(null);

  const load = useCallback(async () => {
    const hours = WINDOWS.find((w) => w.value === win)?.hours ?? 24;
    const r = await request<Stats>('GET', `/v1/admin/stats?hours=${hours}`);
    if (r.ok) setData(r.data);
  }, [request, win]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = (data?.upstream ?? []).reduce(
    (a, r) => ({
      calls: a.calls + r.calls,
      hits: a.hits + r.hits,
      errors: a.errors + r.errors,
      bytes: a.bytes + Number(r.bytes ?? 0),
    }),
    { calls: 0, hits: 0, errors: 0, bytes: 0 },
  );

  return (
    <>
      <div className="mb-3.5 flex items-center gap-2">
        <Seg options={WINDOWS.map((w) => ({ value: w.value, label: w.label }))} value={win} onChange={setWin} />
        <div className="flex-1" />
        <button className="btn btn-sm" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {!data ? (
        <Loading />
      ) : (
        <>
          <div className="mb-3.5 grid gap-3.5" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="panel p-4">
              <div className="num text-[26px] font-semibold">{totals.calls.toLocaleString()}</div>
              <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted">Upstream calls</div>
            </div>
            <div className="panel p-4">
              <div className="num text-[26px] font-semibold">{pct(totals.hits, totals.calls)}</div>
              <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted">Served from cache</div>
              <div className="mt-1.5 text-[11.5px] text-faint">
                {totals.hits.toLocaleString()} of {totals.calls.toLocaleString()} — the rest reached the source
              </div>
            </div>
            <div className="panel p-4">
              <div className={`num text-[26px] font-semibold ${totals.errors ? 'text-bad' : ''}`}>
                {totals.errors.toLocaleString()}
              </div>
              <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted">Failed calls</div>
            </div>
            <div className="panel p-4">
              <div className="num text-[26px] font-semibold">{fmtBytes(totals.bytes)}</div>
              <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted">Fetched</div>
            </div>
          </div>

          <DataBlock title="Calls per hour" sub="the solid part is what the cache answered" raw={data.timeline}>
            <Timeline rows={data.timeline} />
          </DataBlock>

          <DataBlock
            title="Upstream calls"
            sub="by source, operation and what was being asked — the op alone does not say: TecDoc sends five different questions through getArticles"
            raw={data.upstream}
          >
            {data.upstream.length === 0 ? (
              <Empty>Nothing in this window.</Empty>
            ) : (
              <Table cols={['Source', 'Operation', 'Asking', 'Calls', 'Cached', 'Errors', 'Avg', 'Wire p50', 'Wire p95', 'Wire max']}>
                {data.upstream.map((r) => (
                  <tr key={`${r.source}/${r.op}/${r.mode}`}>
                    <td>
                      <SourceChip source={r.source} />
                    </td>
                    <td className="mono">{r.op}</td>
                    <td>
                      <Chip>{r.mode}</Chip>
                    </td>
                    <td className="num">{r.calls.toLocaleString()}</td>
                    <td className="num">{pct(r.hits, r.calls)}</td>
                    <td className={`num ${r.errors ? 'text-bad' : 'text-faint'}`}>{r.errors || '—'}</td>
                    <td className="num text-faint">{r.avgMs}</td>
                    <td className="num">{r.wireP50Ms || '—'}</td>
                    <td className="num">{r.wireP95Ms || '—'}</td>
                    <td className="num text-faint">{r.wireMaxMs || '—'}</td>
                  </tr>
                ))}
              </Table>
            )}
          </DataBlock>

          <DataBlock title="Requests we served" sub="inbound, by route" raw={data.inbound}>
            {data.inbound.length === 0 ? (
              <Empty>Nothing in this window.</Empty>
            ) : (
              <Table cols={['Route', 'Requests', 'Errors', 'Avg', 'p50', 'p95', 'Max', 'Upstream calls', 'With our data']}>
                {data.inbound.map((r) => (
                  <tr key={r.route}>
                    <td className="mono">{r.route}</td>
                    <td className="num">{r.requests.toLocaleString()}</td>
                    <td className={`num ${r.errors ? 'text-bad' : 'text-faint'}`}>{r.errors || '—'}</td>
                    <td className="num text-faint">{r.avgMs}</td>
                    <td className="num">{r.p50Ms}</td>
                    <td className="num">{r.p95Ms}</td>
                    <td className="num text-faint">{r.maxMs}</td>
                    <td className="num text-faint">{r.upstreamCalls.toLocaleString()}</td>
                    <td className="num">{r.withInjection.toLocaleString()}</td>
                  </tr>
                ))}
              </Table>
            )}
          </DataBlock>

          <DataBlock title="Most-shown products" sub="ours, counted every time a search returned them" raw={data.products}>
            {data.products.length === 0 ? (
              <Empty>No product of ours has been returned in this window.</Empty>
            ) : (
              <Table cols={['Brand', 'Article', 'Name', 'Times shown', 'Distinct codes', 'Last']}>
                {data.products.map((p) => (
                  <tr key={p.productId} className="cursor-pointer" onClick={() => go('product', String(p.productId))}>
                    <td>{p.brand}</td>
                    <td className="mono">{p.article}</td>
                    <td className="text-muted">{p.name ?? '—'}</td>
                    <td className="num">{p.hits.toLocaleString()}</td>
                    <td className="num text-faint">{p.codes}</td>
                    <td className="mono text-faint">{new Date(p.lastAt).toLocaleString()}</td>
                  </tr>
                ))}
              </Table>
            )}
          </DataBlock>
        </>
      )}
    </>
  );
}
